import Accessor from "@arcgis/core/core/Accessor";

import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";

import { when, whenOnce } from "@arcgis/core/core/reactiveUtils";
import Onboarding from "../../widgets/Onboarding/Onboarding";

import appConfigJSON from "../../../config/appConfig.json";
import AppTypeTile from "../../widgets/AppTypeTile/AppTypeTile";
import Collection from "@arcgis/core/core/Collection";
import SearchBox from "../../widgets/SearchBox/SearchBox";
import AppTile from "../../widgets/AppTile/AppTile";
import {
  isEnterprise,
  isMobileView,
  isNoResourceInQueryString,
} from "../../utils/appUtils";
import FilterChips, { IChipState } from "../../widgets/FilterChips/FilterChips";
import ItemLoaderViewModel from "../../data/ItemLoader/ItemLoaderViewModel";
import MyAppsViewModel from "../../data/MyApps/MyAppsViewModel";
import AppTypeInfoViewModel from "../../data/AppTypeInfo/AppTypeInfoViewModel";

import t9nAppMessages from "../../../t9n/App/App.json";

import Telemetry from "@esri/telemetry";
import { getLocale } from "@arcgis/core/intl";
import { addUrlParam } from "../../utils/urlUtils";
import PopoverManager from "src/app/widgets/PopoverOnboarding/PopoverManager";

export type sortFields =
  | "created"
  | "appTypeTitle"
  | "compatibleMsgs"
  | "badge" // "Browse All" and "Featured"
  | "modified"
  | "title"
  | "owner"; // "My Apps"

/// NOTE: QueryStringViewModel.ts hard codes these strings
export type navbarRoute = "browseAll" | "create" | "featured" | "myApps";

export type sortPair = [string, sortFields];

const DEFAULT_SORT_FIELD: sortFields = isNoResourceInQueryString()
  ? "appTypeTitle"
  : "compatibleMsgs"; // "Browse All" and "Featured" Default
export const DEFAULT_SORT_MYAPPS: sortFields = "modified"; // "My Apps" Default

// FYI: https://devtopia.esri.com/WebGIS/arcgis-template-landing/pull/264#discussion_r677731
export const HIGH_VAL_UNICODE_CHAR = "\uFFFF";

@subclass("Landing.ui.NavBar.NavBarViewModel")
export default class NavBarViewModel extends Accessor {
  @property()
  navigationRoute: navbarRoute;

  @property()
  sortField: sortFields;

  @property()
  selectedSortField: string;

  @property()
  readonly filterByList: __esri.Collection<[string, string]> = new Collection();

  @property()
  readonly appStringSearch: string;

  @property()
  searchBox: SearchBox;

  @property()
  appMessages: typeof t9nAppMessages;

  @property()
  filterChips: FilterChips = new FilterChips();

  @property()
  itemLoaderVM: ItemLoaderViewModel = null;

  @property()
  myAppsDataVM: MyAppsViewModel = null;

  @property()
  appTypeInfoVM: AppTypeInfoViewModel = null;

  @property()
  portal: __esri.Portal;

  @property()
  telemetry: Telemetry;

  @property()
  suggestionsButtonSignal: boolean =
    window.location.search.indexOf("group=") === -1 && !isMobileView()
      ? true
      : false;

  @property()
  rightSidebarSignal: boolean = true;

  @property()
  learnMoreSidebarSignal: boolean = false;
  @property()
  learnMoreSidebarSelectedIndex: number;

  @property()
  uniqueRenderingTimeStamp: string;

  @property()
  popoverManager: PopoverManager;

  // State variables for Search Box filters
  private _myAppsFilterState: string;

  // State variables for sort selection
  private _browseFeatSortState: sortFields; // Browse All and Featured Tabs sort state
  private _myAppsSortState: sortFields;

  /**
   * Kinda weird putting this in this file, but have to for communication between Sidebar and FeaturedArticle
   * A global store would definitely resolve this
   */
  @property() _featuredArticleStateRouter:
    | "sidebar"
    | "chartViewer"
    | "theming"
    | "insets"
    | "atlas"
    | "manager"
    | "reporter" = "reporter";

  constructor(params?: any) {
    super(params);
  }

  initialize() {
    this.filterChips.filterByList = this.filterByList;

    when(
      () => this?.appMessages !== undefined,
      () => {
        this._setSearchSelectionWithTags(this.appMessages?.tags);
      },
      { once: true, initial: true }
    );

    this.filterByList.on("change", () => {
      this.uniqueRenderingTimeStamp = this.getUniqueTimeStamp();
    });

    whenOnce(() => this.popoverManager).then(() => {
      this.popoverManager.watch(["learnMoreSignalFromManager"], () => {
        if (this.popoverManager.learnMoreSignalFromManager === true) {
          this.learnMoreSidebarSignal = true;
          this.learnMoreSidebarSelectedIndex =
            this.popoverManager.currentPopoverIndex;
          this.popoverManager.learnMoreSignalFromManager = false;
        }
      });

      this.popoverManager.watch(["currentPopoverIndex"], () => {
        this.learnMoreSidebarSelectedIndex =
          this.popoverManager.currentPopoverIndex;
      });
    });
  }

  setNavbarRoute(route: navbarRoute) {
    this.navigationRoute = route;
    const newUrl = addUrlParam("tab", route);
    history.pushState({}, null, newUrl);

    if (route === "featured" && !isMobileView()) {
      // if featured tab and not mobile view, open right sidebar
      this.suggestionsButtonSignal = true;
      if (this.learnMoreSidebarSignal) {
        this.rightSidebarSignal = false;
      }
    }
  }

  openLearnMoreOnboarding(): void {
    this.learnMoreSidebarSignal = !this.learnMoreSidebarSignal;
  }

  toggleSuggBtnSignal(): void {
    this.suggestionsButtonSignal = !this.suggestionsButtonSignal;
    this.clearAllCapabilities();
  }

  toggleRightSidebarSignal(): void {
    this.rightSidebarSignal = !this.rightSidebarSignal;
  }

  isFeaturedTab(): boolean {
    return this.navigationRoute === "featured";
  }

  sortTiles(sortField: sortFields, tiles: AppTypeTile[] | AppTile[]) {
    const locale = getLocale();

    /** Resolve value based on tile type */
    function _decideVal(tile: AppTypeTile | AppTile, field: string): any {
      if (tile.identifier === "AppTile") {
        let value = tile?.appInfo?.[field];
        if (typeof value === "string") {
          value = value?.toLocaleLowerCase(locale); // for title field, sort needs as all lowercase
        }
        return value;
      } else if (tile.identifier === "AppTypeTile") {
        return tile?.appTypeInfo?.[field];
      } else {
        return null;
      }
    }

    if (sortField === "title" || sortField === "appTypeTitle") {
      tiles.sort((a, b) => {
        const aField = _decideVal(a, sortField) ?? HIGH_VAL_UNICODE_CHAR;
        const bField = _decideVal(b, sortField) ?? HIGH_VAL_UNICODE_CHAR;
        return aField?.localeCompare(bField, locale);
      });
    } else if (sortField === "created" || sortField === "modified") {
      tiles.sort((a, b) => {
        const aField = _decideVal(a, sortField) ?? HIGH_VAL_UNICODE_CHAR;
        const bField = _decideVal(b, sortField) ?? HIGH_VAL_UNICODE_CHAR;
        if (aField < bField) {
          return -1;
        } else if (aField > bField) {
          return 1;
        } else {
          return 0;
        }
      });
      tiles.reverse();
    } // sort above puts dates from lowest to highest, so just reverse to get most recent dates first
    else if (sortField === "badge") {
      tiles.sort((a, b) => {
        let aField: string = a.appTypeInfo[sortField] ?? HIGH_VAL_UNICODE_CHAR;
        let bField: string = b.appTypeInfo[sortField] ?? HIGH_VAL_UNICODE_CHAR;
        aField = this.appMessages.appBadges[aField] ?? HIGH_VAL_UNICODE_CHAR;
        bField = this.appMessages.appBadges[bField] ?? HIGH_VAL_UNICODE_CHAR;
        return aField?.localeCompare(bField, locale);
      });
    } else if (sortField === "compatibleMsgs") {
      tiles.sort((a, b) => {
        const aCompatMsgExists: boolean =
          a?.appTypeInfo?.[sortField]?.length > 0;
        const bCompatMsgExists: boolean =
          b?.appTypeInfo?.[sortField]?.length > 0;
        if (aCompatMsgExists === bCompatMsgExists) {
          // both have compat msgs or both don't, then sort alphabetically
          const aUrlField: string =
            a?.appTypeInfo?.appTypeTitle ?? HIGH_VAL_UNICODE_CHAR;
          const bUrlField: string =
            b?.appTypeInfo?.appTypeTitle ?? HIGH_VAL_UNICODE_CHAR;
          return aUrlField?.localeCompare(bUrlField, locale);
        } else if (!aCompatMsgExists) {
          return -1;
        } else if (!bCompatMsgExists) {
          return 1;
        } else {
          return 0;
        }
      });
    }
  }

  addCapabililty([translationKey, selectionValue]: [string, string]) {
    const result = this.filterChips.viewModel.addChip(
      selectionValue,
      "capability"
    );
    if (result.success) {
      this.filterByList.add([translationKey, selectionValue]);
    }
  }

  clearAllCapabilities() {
    this.filterChips.viewModel.chips.removeAll();
    this.filterByList.removeAll();
  }

  appSearch(str: string) {
    switch (this.navigationRoute) {
      case "myApps":
        this._myAppsFilterState = str;
        this.myAppsDataVM.setFilterAndRequery(str);
        break;
    }
    this.set("appStringSearch", str);
  }

  setCurrentSearchFilter() {
    const { search_appTypes, search_apps } = this.appMessages.searchBox;
    const { navigationRoute, _myAppsFilterState } = this;
    switch (navigationRoute) {
      case "browseAll":
        this.searchBox?.setSearchBoxString("", search_appTypes, true);
        break;
      case "featured":
        this.searchBox?.setSearchBoxString("", search_appTypes, true);
        break;
      case "myApps":
        this.searchBox?.setSearchBoxString(
          _myAppsFilterState != null ? _myAppsFilterState : "",
          search_apps,
          false
        );
        break;
    }
  }

  /** Sets the sort for the current tab. If there is a saved sort, sets that, otherwise uses default sort for tab */
  setCurrentSort(_sortTermsToSortFields: [string, sortFields][]): void {
    let sortField: sortFields;
    switch (this.navigationRoute) {
      case "browseAll":
        sortField =
          this._browseFeatSortState != null
            ? this._browseFeatSortState
            : DEFAULT_SORT_FIELD;
        break;
      case "featured":
        sortField =
          this._browseFeatSortState != null
            ? this._browseFeatSortState
            : DEFAULT_SORT_FIELD;
        break;
      case "myApps":
        sortField =
          this._myAppsSortState != null
            ? this._myAppsSortState
            : DEFAULT_SORT_MYAPPS;
        break;
    }
    this.sortField = sortField;
    this.selectedSortField = _sortTermsToSortFields.find(
      (termToField) => termToField[1] === sortField
    )[0];
  }

  handleSortSelectionChange(e: any) {
    const selectedItem: HTMLCalciteDropdownItemElement =
      e?.target?.selectedItems?.[0];
    this.sortField = (selectedItem as any)?.value;
    this.selectedSortField = selectedItem?.textContent;

    switch (this.navigationRoute) {
      case "browseAll":
        this._browseFeatSortState = this.sortField;
        break;
      case "featured":
        this._browseFeatSortState = this.sortField;
        break;
      case "myApps":
        this._myAppsSortState = this.sortField;
        this.myAppsDataVM.setSortAndRequery(this.sortField);
        break;
    }
  }

  getUniqueTimeStamp(): string {
    return new Date().toISOString();
  }

  /** Use the App Type Item's Tags to populate the Search Boxes' suggestions */
  private _setSearchSelectionWithTags(t9nObj: Object) {
    when(
      () => this?.searchBox !== undefined,
      () => {
        this.searchBox.viewModel.setSearchSuggestions(t9nObj);
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  decideBrowseAllAndFeatSorts(): sortPair[] {
    const [Compat, ReleaseDate, Title, Badge] = this._generateSortPairs();
    const isNoResourceWorkflow: boolean = isNoResourceInQueryString();
    if (!isEnterprise(this.portal)) {
      // AGOL
      if (isNoResourceWorkflow) {
        // No Resource Workflow
        return [ReleaseDate, Title, Badge];
      } else {
        // Resource Workflow
        return [Compat, ReleaseDate, Title, Badge];
      }
    } else {
      // Enterprise
      if (isNoResourceWorkflow) {
        // No Resource Workflow
        return [ReleaseDate, Title];
      } else {
        // Resource Workflow
        return [Compat, ReleaseDate, Title];
      }
    }
  }

  private _generateSortPairs(): [sortPair, sortPair, sortPair, sortPair] {
    const { sortingTerms } = this.appMessages;
    return [
      [sortingTerms.compatibility, "compatibleMsgs"],
      [sortingTerms.releaseDate, "created"],
      [sortingTerms.name, "appTypeTitle"],
      [sortingTerms.badges, "badge"],
    ];
  }
}
